import { returnAllTradeDetailsFromGenericParams } from "@/lib/agGridColDefs/colDefGlobalHelpers";
import { InstrumentTypeCode } from "@/lib/interfaces and Enums/ENUMInstrumentTypeCode";
import tradeDataStore from "@/store/tradeDataStore";
import {
  isFillCancelled,
  isFillLocked,
  isFillCellsEditable,
  getFillFromStore,
} from "../../fillStateHelpers";

/**
 * Checks if the Fill Quantity cell should be editable.
 * Editable when:
 * - Trade role is "RP"
 * - Fill is NOT cancelled
 * - Fill is NOT locked (has timestamp) OR is in "Updated (Pending)" state
 *
 * @param params - AG Grid cell params
 * @returns boolean indicating if the cell should be editable
 */
export function isFillQuantityCellEditable(params: any): boolean {
  if (!params.data) {
    return false;
  }

  const { fullTradeData } = returnAllTradeDetailsFromGenericParams(params);

  if (!fullTradeData) {
    return false;
  }

  // Only RP role can edit fill quantities
  if (fullTradeData.role !== "RP") {
    return false;
  }

  // Get the fill from the store to check its state
  const esid = fullTradeData.esid;
  const blockId = params.data.blockId;
  const fillId = params.data.fillId;
  const extFillId = params.data.fillData?.extFillId;

  if (!esid || blockId === undefined || fillId === undefined) {
    return false;
  }

  const tradeData = tradeDataStore.getState().tradeData;
  const fill = getFillFromStore(tradeData, esid, blockId, fillId, extFillId);

  if (!fill) {
    return false;
  }

  // Cancelled fills are not editable
  if (isFillCancelled(fill)) {
    return false;
  }

  // Locked fills (with timestamp) are not editable unless in "Updated (Pending)" state
  if (isFillLocked(fill)) {
    return false;
  }

  // Check if fill is in an editable state (New or Updated)
  return isFillCellsEditable(fill);
}

/**
 * Gets the current fill quantity value from the Zustand store
 * for the specified fillId within a trade's legs fills array.
 *
 * @param esid - The execution strategy ID
 * @param fillId - The fill ID to find
 * @param blockId - The block/leg ID
 * @returns The fill quantity value or null
 */
export function getFillQuantityFromStore(
  esid: string,
  fillId: string | number,
  blockId: number,
): number | null {
  const tradeData = tradeDataStore.getState().tradeData;
  const trade = tradeData.find((t) => t.esid === esid);

  if (!trade) {
    return null;
  }

  // For RP trades, fills are in the legs array
  if (trade.role === "RP" && trade.legs) {
    const leg = trade.legs.find((l) => l.blockId === blockId);
    if (leg?.fills) {
      const fill = leg.fills.find((f) => f.fillId === fillId);
      return fill?.fillSize ?? null;
    }
  }

  // For OP trades, fills are in executionRequestCounterParties > legs
  if (trade.role === "OP" && trade.executionRequestCounterParties) {
    for (const cp of trade.executionRequestCounterParties) {
      if (cp.legs) {
        const leg = cp.legs.find((l) => l.blockId === blockId);
        if (leg?.fills) {
          const fill = leg.fills.find((f) => f.fillId === fillId);
          if (fill) {
            return fill.fillSize ?? null;
          }
        }
      }
    }
  }

  return null;
}

/**
 * Gets the instrument type from the Zustand store for a given leg.
 * Used for formatting quantities with appropriate notation (e.g., "lots" for FUT).
 *
 * @param esid - The execution strategy ID
 * @param blockId - The block/leg ID
 * @returns The instrument type string or undefined
 */
export function getInstrumentTypeFromStore(
  esid: string,
  blockId: number,
): string | undefined {
  const tradeData = tradeDataStore.getState().tradeData;
  const trade = tradeData.find((t) => t.esid === esid);

  if (!trade) {
    return undefined;
  }

  // For RP trades, legs are in the legs array
  if (trade.role === "RP" && trade.legs) {
    const leg = trade.legs.find((l) => l.blockId === blockId);
    return leg?.instrumentType;
  }

  // For OP trades, legs are in executionRequestCounterParties > legs
  if (trade.role === "OP" && trade.executionRequestCounterParties) {
    for (const cp of trade.executionRequestCounterParties) {
      if (cp.legs) {
        const leg = cp.legs.find((l) => l.blockId === blockId);
        if (leg) {
          return leg.instrumentType;
        }
      }
    }
  }

  return undefined;
}

/**
 * Checks if a fill's block is a future instrument type.
 * Used to determine if decimal values should be allowed for fill quantities.
 *
 * @param esid - The execution strategy ID
 * @param blockId - The block/leg ID
 * @returns true if the block is a FUT instrument type, false otherwise
 */
export function isFillBlockFuture(esid: string, blockId: number): boolean {
  const instrumentType = getInstrumentTypeFromStore(esid, blockId);
  return instrumentType === InstrumentTypeCode.FUT;
}

/**
 * Updates the fill quantity in the Zustand store.
 * - For "New" fills: keeps fillType as "New", no historic values needed
 * - For unchanged/backend fills: sets fillType to "Updated" and preserves historic values
 * - For already "Updated" fills: keeps fillType, doesn't overwrite historic values
 *
 * @param esid - The execution strategy ID
 * @param fillId - The fill ID to update
 * @param blockId - The block/leg ID
 * @param newQuantity - The new quantity value
 */
export function updateFillQuantityInStore(
  esid: string,
  fillId: string | number,
  blockId: number,
  newQuantity: number | undefined,
): void {
  const tradeData = tradeDataStore.getState().tradeData;
  const tradeIndex = tradeData.findIndex((t) => t.esid === esid);

  if (tradeIndex === -1) {
    return;
  }

  const trade = tradeData[tradeIndex];

  // Only RP can edit fills
  if (trade.role !== "RP" || !trade.legs) {
    return;
  }

  const newLegs = trade.legs.map((leg) => {
    if (leg.blockId !== blockId || !leg.fills) {
      return leg;
    }

    const newFills = leg.fills.map((fill) => {
      if (fill.fillId === fillId) {
        // New fills stay as "New", just update the value
        if (fill.fillType === "New") {
          return { ...fill, fillSize: newQuantity ?? fill.fillSize };
        }

        // For fills from backend (unchanged or already updated):
        // - Set fillType to "Updated"
        // - Preserve historic values if not already set
        return {
          ...fill,
          fillSize: newQuantity ?? fill.fillSize,
          fillType: "Updated" as const,
          // Only set historic value if not already preserved
          historicFillRate:
            fill.historicFillRate !== undefined
              ? fill.historicFillRate
              : fill.fillRate,
          historicFillSize:
            fill.historicFillSize !== undefined
              ? fill.historicFillSize
              : fill.fillSize,
        };
      }
      return fill;
    });

    return { ...leg, fills: newFills };
  });

  const newTrade = { ...trade, legs: newLegs };
  const newTradeData = tradeData.map((t, i) =>
    i === tradeIndex ? newTrade : t,
  );

  tradeDataStore.getState().updateTradeData(newTradeData);
}

/**
 * Determines styling based on whether the cell is editable (RP role).
 *
 * @param isRP - Whether the trade role is RP
 * @param theme - Current theme (light/dark)
 * @returns CSS class string for styling
 */
export function getFillQuantityCellStyling(
  isRP: boolean,
  theme: string | undefined,
): string {
  if (!isRP) {
    return "";
  }

  const safeTheme = theme ?? "light";
  return safeTheme === "light" ? "inputCellLight" : "inputCellDark";
}

/**
 * Gets the notionalLeg1 (block size) from the Zustand store for a given leg.
 * Used for validating that fill quantities don't exceed the block size.
 *
 * @param esid - The execution strategy ID
 * @param blockId - The block/leg ID
 * @returns The notionalLeg1 value or null
 */
export function getBlockNotionalFromStore(
  esid: string,
  blockId: number,
): number | null {
  const tradeData = tradeDataStore.getState().tradeData;
  const trade = tradeData.find((t) => t.esid === esid);

  if (!trade) {
    return null;
  }

  // For RP trades, legs are in the legs array
  if (trade.role === "RP" && trade.legs) {
    const leg = trade.legs.find((l) => l.blockId === blockId);
    return leg?.notionalLeg1 ?? null;
  }

  // For OP trades, legs are in executionRequestCounterParties > legs
  if (trade.role === "OP" && trade.executionRequestCounterParties) {
    for (const cp of trade.executionRequestCounterParties) {
      if (cp.legs) {
        const leg = cp.legs.find((l) => l.blockId === blockId);
        if (leg) {
          return leg.notionalLeg1 ?? null;
        }
      }
    }
  }

  return null;
}

/**
 * Gets the total fill quantity for a block, excluding a specific fill.
 * Used for validating that the new fill quantity won't exceed the block size.
 * Also excludes cancelled fills from the total.
 *
 * @param esid - The execution strategy ID
 * @param blockId - The block/leg ID
 * @param excludeFillId - The fill ID to exclude from the total
 * @returns The total fill quantity excluding the specified fill and cancelled fills
 */
export function getTotalFillQuantityExcludingCurrent(
  esid: string,
  blockId: number,
  excludeFillId: string | number,
): number {
  const tradeData = tradeDataStore.getState().tradeData;
  const trade = tradeData.find((t) => t.esid === esid);

  if (!trade) {
    return 0;
  }

  // For RP trades, fills are in the legs array
  if (trade.role === "RP" && trade.legs) {
    const leg = trade.legs.find((l) => l.blockId === blockId);
    if (leg?.fills) {
      return leg.fills
        .filter((f) => f.fillId !== excludeFillId && !isFillCancelled(f))
        .reduce((sum, f) => sum + (f.fillSize || 0), 0);
    }
  }

  // For OP trades, fills are in executionRequestCounterParties > legs
  if (trade.role === "OP" && trade.executionRequestCounterParties) {
    for (const cp of trade.executionRequestCounterParties) {
      if (cp.legs) {
        const leg = cp.legs.find((l) => l.blockId === blockId);
        if (leg?.fills) {
          return leg.fills
            .filter((f) => f.fillId !== excludeFillId && !isFillCancelled(f))
            .reduce((sum, f) => sum + (f.fillSize || 0), 0);
        }
      }
    }
  }

  return 0;
}
